import { useState, useEffect } from 'react';
import { supabase, Notice } from '@/lib/supabase';
import { checkAdminAuth } from '@/pages/admin/components/AdminLogin';

const CATEGORIES = ['공지', '모집', '교육', '자격증', '행사'];

interface NoticeFormData {
  title: string;
  content: string;
  category: string;
  important: boolean;
}

const emptyForm: NoticeFormData = { title: '', content: '', category: '공지', important: false };

export default function NoticeTab() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [form, setForm] = useState<NoticeFormData>(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const isAdmin = checkAdminAuth();

  const fetchNotices = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('notices')
      .select('*')
      .order('created_at', { ascending: false });
    setNotices(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchNotices(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    if (editId !== null) {
      await supabase.from('notices').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editId);
    } else {
      await supabase.from('notices').insert([form]);
    }
    setSaving(false);
    setForm(emptyForm);
    setEditId(null);
    setShowAdmin(false);
    fetchNotices();
  };

  const handleEdit = (n: Notice) => {
    setForm({ title: n.title, content: n.content || '', category: n.category, important: n.important });
    setEditId(n.id);
    setShowAdmin(true);
    setExpandedId(null);
  };

  const handleDelete = async (id: number) => {
    await supabase.from('notices').delete().eq('id', id);
    setDeleteConfirmId(null);
    fetchNotices();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowAdmin(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">총 {notices.length}개의 공지사항</p>
        {isAdmin && (
          <button
            onClick={() => { setShowAdmin(!showAdmin); setForm(emptyForm); setEditId(null); }}
            className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#1B3A6B] text-white text-sm font-semibold cursor-pointer hover:bg-[#15305a] transition-colors"
          >
            <i className="ri-add-line" /> 공지 작성
          </button>
        )}
      </div>

      {isAdmin && showAdmin && (
        <form onSubmit={handleSubmit} className="mb-8 bg-[#F4F6FA] rounded-xl p-6 border border-gray-200">
          <h3 className="text-sm font-bold text-[#1B3A6B] mb-4">{editId ? '공지 수정' : '새 공지 작성'}</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white cursor-pointer focus:outline-none focus:border-[#1B3A6B]"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.important}
                  onChange={(e) => setForm({ ...form, important: e.target.checked })}
                  className="cursor-pointer"
                />
                중요 공지
              </label>
            </div>
            <input
              type="text"
              placeholder="제목을 입력하세요"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              className="w-full text-sm border border-gray-200 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#1B3A6B] bg-white"
            />
            <textarea
              placeholder="내용을 입력하세요 (선택)"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={4}
              maxLength={500}
              className="w-full text-sm border border-gray-200 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#1B3A6B] bg-white resize-none"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap px-5 py-2 rounded-md bg-[#1B3A6B] text-white text-sm font-semibold cursor-pointer hover:bg-[#15305a] transition-colors disabled:opacity-50"
            >
              {saving ? '저장 중...' : editId ? '수정 완료' : '등록'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="whitespace-nowrap px-5 py-2 rounded-md border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#1B3A6B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : notices.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">등록된 공지사항이 없습니다.</div>
      ) : (
        <div className="space-y-2">
          {notices.map((n) => (
            <div key={n.id} className="border border-gray-100 rounded-lg overflow-hidden">
              <div
                className="p-4 md:p-5 hover:bg-[#F4F6FA] transition-all cursor-pointer group"
                onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
              >
                {/* 모바일: 세로 레이아웃 / 데스크탑: 가로 레이아웃 */}
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                  {/* 상단: 카테고리 + 제목 */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-sm whitespace-nowrap ${n.important ? 'bg-[#C9A84C] text-white' : 'bg-gray-100 text-gray-500'}`}>
                      {n.category}
                    </span>
                    <p className="text-gray-800 text-sm font-medium group-hover:text-[#1B3A6B] transition-colors leading-relaxed">{n.title}</p>
                  </div>
                  
                  {/* 하단: 날짜 + 관리자 버튼 + 화살표 */}
                  <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4">
                    <span className="text-gray-400 text-xs whitespace-nowrap">
                      {new Date(n.created_at).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace('.', '')}
                    </span>
                    {isAdmin && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEdit(n)}
                          className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-[#1B3A6B] hover:bg-[#1B3A6B]/10 cursor-pointer transition-colors"
                          title="수정"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(n.id)}
                          className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                          title="삭제"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    )}
                    {expandedId === n.id
                      ? <i className="ri-arrow-up-s-line text-gray-300 group-hover:text-[#1B3A6B] transition-colors" />
                      : <i className="ri-arrow-down-s-line text-gray-300 group-hover:text-[#1B3A6B] transition-colors" />
                    }
                  </div>
                </div>
              </div>
              {expandedId === n.id && n.content && (
                <div className="px-4 md:px-6 pb-5 bg-[#F4F6FA] border-t border-gray-100">
                  <p className="text-gray-600 text-sm leading-relaxed pt-4 whitespace-pre-wrap">{n.content}</p>
                </div>
              )}
              {isAdmin && deleteConfirmId === n.id && (
                <div className="px-4 md:px-6 py-4 bg-red-50 border-t border-red-100 flex items-center justify-between">
                  <p className="text-red-600 text-sm font-medium">정말 삭제하시겠습니까?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(n.id)}
                      className="whitespace-nowrap px-4 py-1.5 rounded-md bg-red-500 text-white text-xs font-semibold cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      삭제
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(null)}
                      className="whitespace-nowrap px-4 py-1.5 rounded-md border border-gray-200 text-gray-600 text-xs font-semibold cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      취소
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
